// HYD conference-room mailbox addresses. Keyed by the roomColor we already
// compute below, so the parser can attach the right `email` to each bookable
// room and the booking buttons in SeatingChart can target the room mailbox
// directly (without needing CSV entries for these rooms).
const HYD_ROOM_EMAILS = {
  red:      'HYD.Red@pinnaclereliability.com',
  blue:     'HYD.Blue@pinnaclereliability.com',
  green:    'HYD.Green@pinnaclereliability.com',
  large:    'HYD.Large@pinnaclereliability.com',
  training: 'HYD.Training@pinnaclereliability.com',
  huddle:   'HYD.HuddleArea@pinnaclereliability.com',
};

// Older HYD admin edits compacted spaces out of non-workstation room ids
// ("HR ROOM" -> "HRROOM"), which breaks matching against drawio labels.
// Normalize those legacy variants back to the visible room ids.
const HYD_LOCATION_ALIAS_MAP = {
  HRROOM: 'HR ROOM',
  HUDDLEAREA: 'HUDDLE AREA',
  BLUEMEETINGROOM: 'BLUE MEETING ROOM',
  REDMEETINGROOM: 'RED MEETING ROOM',
  GREENMEETINGROOM: 'GREEN MEETING ROOM',
  LARGEMEETINGROOM: 'LARGE MEETING ROOM',
};

const normalizeHydLocation = (raw) => {
  if (!raw) return '';
  const trimmed = raw.trim().replace(/\s+/g, ' ').toUpperCase();
  if (!trimmed) return '';
  if (/^\d+$/.test(trimmed)) return `WS${trimmed}`;
  if (/^WS\s*\d+$/i.test(trimmed)) return trimmed.replace(/\s+/g, '');

  const compact = trimmed.replace(/\s+/g, '');
  return HYD_LOCATION_ALIAS_MAP[compact] || trimmed;
};

// Strip HTML tags and decode entities from a label string. HYD drawio labels
// arrive wrapped like <span style="font-family: Verdana;">WS028</span> — we want
// just "WS028". Pasadena labels are plain text, so this is a no-op there.
const stripHtmlLabel = (raw) => {
  if (!raw) return '';
  const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
  const text = doc.body.textContent || '';
  return text.replace(/\s+/g, ' ').trim();
};

// Utility to parse draw.io XML files and extract room/wall data.
//
// Cache strategy: rely on Express's built-in ETag + maxAge: '1d' for these
// static assets. The browser revalidates with If-None-Match — fast 304 when
// unchanged, full content when the file moves on disk. A `?v=Date.now()`
// cache-buster used to live here; it defeated cache entirely (every load
// re-downloaded the 124KB HYD plan), which was the dominant cause of slow
// campus switches. If you need to force-refresh after a deploy, hard reload
// (Ctrl+Shift+R) or bust via filename hash in the build pipeline.
export const parseDrawioFile = async (filePath) => {
  const response = await fetch(filePath);
  const xmlText = await response.text();

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

  const rooms = [];
  const cubes = [];
  const conferenceRooms = [];
  const facilities = [];
  const walls = [];

  // Extract building name from file path (e.g., '/floorplans/building6.drawio' -> 'building6')
  const buildingName = filePath.split('/').pop().replace('.drawio', '');

  // Regex patterns for different object types
  const officePattern = /^\d+\.O\.\d+$/;
  const cubePattern = /^\d+\.C\.\d+$/;
  const conferenceRoomPattern = /^\d+\.CR\./;
  // HYD workstations: WS123, "WS 015" (space variant)
  const hydWorkstationPattern = /^WS\s*\d+$/;
  // HYD conference / meeting rooms — descriptive labels, not numbered
  const hydConferenceRoomPattern = /^(MEETING ROOM|LARGE MEETING ROOM|HUDDLE AREA|HR ROOM|M\.CABIN|CABIN)\b/i;
  // HYD lockers / storage cubicles — many repeated copies, render as plain boxes
  const hydLockerPattern = /^LWA-\d+$/i;

  // Find all object elements (rooms with label text)
  const objects = xmlDoc.querySelectorAll('object');
  objects.forEach(obj => {
    // Strip any HTML wrapping so HYD's <span>WS028</span> matches WS028
    const roomId = stripHtmlLabel(obj.getAttribute('label'));
    if (roomId) {
      const mxCell = obj.querySelector('mxCell');
      if (mxCell) {
        const geometry = mxCell.querySelector('mxGeometry');
        if (geometry) {
          // Normalize HYD workstation ids: "WS 015" → "WS015" so CSV lookups
          // match regardless of the source spacing.
          const normalizedId = hydWorkstationPattern.test(roomId)
            ? roomId.replace(/\s+/g, '')
            : roomId;

          const elementData = {
            id: normalizedId,
            left: parseFloat(geometry.getAttribute('x')) || 0,
            top: parseFloat(geometry.getAttribute('y')) || 0,
            width: parseFloat(geometry.getAttribute('width')) || 0,
            height: parseFloat(geometry.getAttribute('height')) || 0,
            people: [] // Will be populated from separate data source
          };

          // Check if this is an office
          if (officePattern.test(roomId)) {
            rooms.push(elementData);
          }
          // Check if this is a cube/desk (Pasadena format)
          else if (cubePattern.test(roomId)) {
            cubes.push(elementData);
          }
          // HYD workstation (treated as a cube/desk). Show only the numeric
          // part on the floor plan ("WS059" → "059") to save horizontal room.
          else if (hydWorkstationPattern.test(roomId)) {
            const displayLabel = normalizedId.replace(/^WS/, '');
            cubes.push({ ...elementData, displayLabel });
          }
          // Pasadena conference room
          else if (conferenceRoomPattern.test(roomId)) {
            // Extract display name (everything after "CR.")
            const displayName = roomId.split('.CR.')[1];
            conferenceRooms.push({ ...elementData, displayName });
          }
          // HYD meeting / huddle / HR rooms — full label is the display name.
          // Also catches colour-prefixed rooms (BLUE/RED/GREEN MEETING ROOM) and
          // the HYD training room which include a capacity suffix like "6Per".
          else if (
            hydConferenceRoomPattern.test(roomId) ||
            /MEETING ROOM/i.test(roomId) ||
            /^HYD\s+TRAN/i.test(roomId)
          ) {
            // Strip trailing capacity like "6Per", "11Per", "4Per" (may be
            // attached without a space due to <br> stripping).
            const capacityMatch = roomId.match(/(\d+)Per\s*$/i);
            const capacity = capacityMatch ? parseInt(capacityMatch[1], 10) : null;
            const displayName = roomId.replace(/\s*\d+Per\s*$/i, '').replace(/\d+Per\s*$/i, '').trim();
            // Assign a colour key so the floor plan can tint each room.
            let roomColor = null;
            if (/^BLUE\s/i.test(roomId))        roomColor = 'blue';
            else if (/^RED\s/i.test(roomId))    roomColor = 'red';
            else if (/^GREEN\s/i.test(roomId))  roomColor = 'green';
            else if (/^LARGE\s/i.test(roomId))  roomColor = 'large';
            else if (/^HYD\s/i.test(roomId))    roomColor = 'training';
            else if (/^HUDDLE/i.test(roomId))   roomColor = 'huddle';
            else if (/^HR ROOM/i.test(roomId))  roomColor = 'hr';
            else if (/^CABIN/i.test(roomId))    roomColor = 'cabin';
            // Bookable conference rooms (have M365 calendars, can be reserved):
            // HYD Training Room + Large/Blue/Red/Green Meeting Room + Huddle Area.
            const isBookable = /MEETING ROOM/i.test(roomId) || /^HYD\s+TRAN/i.test(roomId) || /^HUDDLE/i.test(roomId);
            // Personal/occupied spaces — render the people inside, never bookable.
            // (Cabins, HR Room, M.Cabin.)
            const isPersonalSpace = /^(CABIN|M\.CABIN|HR ROOM)/i.test(roomId);
            // Attach the room mailbox so the booking buttons and availability
            // fetch can target it without relying on CSV entries.
            const email = isBookable ? HYD_ROOM_EMAILS[roomColor] || null : null;
            conferenceRooms.push({ ...elementData, displayName, roomColor, isBookable, isPersonalSpace, capacity, email });
          }
          // HYD locker / storage units (LWA-01...LWA-16, many duplicates) —
          // render as plain boxes without labels to reduce visual noise.
          else if (hydLockerPattern.test(roomId)) {
            facilities.push({ ...elementData, type: 'locker', label: '' });
          }
          // Check if this is a repro station (printer, scanner, paper cutter)
          else if (roomId.includes('REPRO')) {
            facilities.push({ ...elementData, type: 'repro', label: 'Repro Station' });
          }
          // Check if this is a cafeteria
          else if (roomId.includes('CAFETERIA')) {
            facilities.push({ ...elementData, type: 'cafeteria', label: 'Cafeteria' });
          }
          // Check if this is a printer room (contains PRINT in all caps)
          else if (roomId.includes('PRINT')) {
            facilities.push({ ...elementData, type: 'printer', label: roomId });
          }
          // Check if this is a coffee bar (contains COFFEE in all caps)
          else if (roomId.includes('COFFEE')) {
            facilities.push({ ...elementData, type: 'coffee', label: roomId });
          }
          // Check if this is a gym (contains GYM in all caps)
          else if (roomId.includes('GYM')) {
            facilities.push({ ...elementData, type: 'gym', label: roomId });
          }
          // Check if this is a market/mini-mart (contains MARKET in all caps)
          else if (roomId.includes('MARKET')) {
            facilities.push({ ...elementData, type: 'market', label: roomId });
          }
          // Check if this is a Peak Store/swag store (contains PEAKSTORE in all caps)
          else if (roomId.includes('PEAKSTORE')) {
            facilities.push({ ...elementData, type: 'peakstore', label: roomId });
          }
          // HYD named areas
          else if (roomId.includes('TOILET')) {
            facilities.push({ ...elementData, type: 'toilet', label: roomId });
          }
          else if (roomId.includes('COLLAB')) {
            facilities.push({ ...elementData, type: 'collab', label: roomId });
          }
          else if (roomId.includes('SERVER')) {
            facilities.push({ ...elementData, type: 'server', label: roomId });
          }
          else if (roomId.includes('REFUGE')) {
            facilities.push({ ...elementData, type: 'refuge', label: roomId });
          }
          else if (roomId.includes('BALCONY')) {
            facilities.push({ ...elementData, type: 'balcony', label: roomId });
          }
          else if (roomId.includes('STAIRS') || roomId.includes('LIFT')) {
            facilities.push({ ...elementData, type: 'stairlift', label: roomId });
          }
          else if (roomId.includes('ELECT') || roomId.includes('AHU')) {
            facilities.push({ ...elementData, type: 'utility', label: roomId });
          }
          else if (roomId.includes('WASH')) {
            facilities.push({ ...elementData, type: 'washing', label: roomId });
          }
          // Everything else is a generic facility
          else {
            facilities.push({ ...elementData, type: 'generic', label: roomId });
          }
        }
      }
    }
  });
  
  // Find all wall and door elements (mxCell with wall or door styles)
  const doors = [];
  const cells = xmlDoc.querySelectorAll('mxCell');
  cells.forEach(cell => {
    const style = cell.getAttribute('style');
    if (style) {
      const geometry = cell.querySelector('mxGeometry');
      if (geometry) {
        let width = parseFloat(geometry.getAttribute('width')) || 0;
        let height = parseFloat(geometry.getAttribute('height')) || 0;
        let left = parseFloat(geometry.getAttribute('x')) || 0;
        let top = parseFloat(geometry.getAttribute('y')) || 0;
        
        // Handle rotation attribute - if rotation=90, swap width/height
        if (style.includes('rotation=90') || style.includes('rotation=-90')) {
          // Swap dimensions for rotated walls
          [width, height] = [height, width];
        }
        
        // Check if it's a wall
        if (style.includes('mxgraph.floorplan.wall')) {
          walls.push({
            left,
            top,
            width,
            height
          });
        }
        // Check if it's a door (single, double, sliding, etc.)
        else if (style.includes('mxgraph.floorplan.door')) {
          doors.push({
            left,
            top,
            width,
            height,
            type: style.includes('doorDouble') ? 'double' : 'single'
          });
        }
        // Entrance text labels (rotation=-90, no fill) — compute visual bounding box
        else if (style.includes('rotation=-90') && style.includes('fillColor=none')) {
          const rawValue = cell.getAttribute('value') || '';
          if (rawValue) {
            const tmpDoc = new DOMParser().parseFromString(`<div>${rawValue}</div>`, 'text/html');
            const plainText = (tmpDoc.body.textContent || '').replace(/\s+/g, ' ').trim();
            if (/entrance/i.test(plainText)) {
              // After swap: width = original_h, height = original_w.
              // Visual top-left after -90° rotation around element center:
              const visLeft = left + (height - width) / 2;
              const visTop  = top  - (height - width) / 2;
              facilities.push({
                id: `entrance-${plainText.replace(/\s+/g, '-').toLowerCase()}`,
                left: visLeft,
                top:  visTop,
                width,
                height,
                type: 'entrance',
                label: plainText
              });
            }
          }
        }
      }
    }
  });
  
  // Calculate the bounding box of all elements to remove unused margins
  const allElements = [...rooms, ...cubes, ...conferenceRooms, ...facilities, ...walls, ...doors];
  if (allElements.length > 0) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    allElements.forEach(element => {
      minX = Math.min(minX, element.left);
      minY = Math.min(minY, element.top);
      maxX = Math.max(maxX, element.left + element.width);
      maxY = Math.max(maxY, element.top + element.height);
    });
    
    // Normalize all coordinates by subtracting the minimum values
    const offsetX = minX;
    const offsetY = minY;
    
    rooms.forEach(room => {
      room.left -= offsetX;
      room.top -= offsetY;
    });
    
    cubes.forEach(cube => {
      cube.left -= offsetX;
      cube.top -= offsetY;
    });
    
    conferenceRooms.forEach(room => {
      room.left -= offsetX;
      room.top -= offsetY;
    });
    
    facilities.forEach(facility => {
      facility.left -= offsetX;
      facility.top -= offsetY;
    });
    
    walls.forEach(wall => {
      wall.left -= offsetX;
      wall.top -= offsetY;
    });
    
    doors.forEach(door => {
      door.left -= offsetX;
      door.top -= offsetY;
    });
    
    // Return the actual used dimensions
    const usedWidth = maxX - minX;
    const usedHeight = maxY - minY;
    
    return {
      building: buildingName,
      name: `Building ${buildingName.replace('building', '')}`,
      scale: 1,
      rooms,
      cubes,
      conferenceRooms,
      facilities,
      walls,
      doors,
      actualWidth: usedWidth,
      actualHeight: usedHeight
    };
  }
  
  return {
    building: buildingName,
    name: `Building ${buildingName.replace('building', '')}`,
    scale: 1,
    rooms,
    cubes,
    conferenceRooms,
    facilities,
    walls,
    doors
  };
};

// Load people data for a given building. Accepts the building object so it can
// pick the right CSV and the right column layout. Falls back to the legacy
// Pasadena CSV when no building is supplied (preserves any older callers).
//
// Options:
//   authFetch — async (url, init?) => Response. Required for seating API reads
//   now that the storage-backed CSV endpoints sit behind JWT.
//
// Returns { [locationId]: [{ name, email }] }.
export const getPeopleData = async (buildingOrName, options = {}) => {
  // Backward compat: a bare string means "Pasadena building name".
  const isLegacyCall = typeof buildingOrName === 'string';
  const csvFormat = isLegacyCall
    ? 'pasadena'
    : (buildingOrName?.csvFormat || 'pasadena');
  const pasadenaBuildingName = isLegacyCall
    ? buildingOrName
    : buildingOrName?.drawioName;
  const seatingOffice = isLegacyCall
    ? null
    : (buildingOrName?.seatingOffice || (csvFormat === 'hyd' ? 'hyd' : null));

  try {
    // Storage-backed offices: load live data from the seating API. Endpoints
    // require an Auth0 JWT, so the caller should supply authFetch.
    if (seatingOffice) {
      const fetcher = options.authFetch || fetch;
      const response = await fetcher(`/api/seating/${seatingOffice}`);
      if (!response.ok) throw new Error(`Seating API returned ${response.status}`);
      const { people } = await response.json();
      if (csvFormat === 'hyd') {
        return parseHydPeopleRecords(people);
      }
      return parsePasadenaPeopleRecords(people, pasadenaBuildingName);
    }

    // Legacy Pasadena call: load from static CSV as before.
    const csvUrl = isLegacyCall
      ? '/floorplans/floorplanData.csv'
      : (buildingOrName?.peopleCsv || '/floorplans/floorplanData.csv');
    const response = await fetch(csvUrl);
    const csvText = await response.text();
    const lines = csvText.trim().split('\n');
    return parsePasadenaPeopleCsv(lines, pasadenaBuildingName);
  } catch (error) {
    console.error('Error loading people data:', error);
    return {};
  }
};

const parsePasadenaPeopleRecords = (people, buildingName) => {
  const peopleByLocation = {};
  (people || []).forEach(person => {
    const building = person.building;
    const location = person.location;
    const name = person.name;
    const email = person.email;

    if (building === buildingName && name && name.trim()) {
      if (!peopleByLocation[location]) {
        peopleByLocation[location] = [];
      }
      peopleByLocation[location].push({
        name: name.trim(),
        email: email ? email.trim() : ''
      });
    }
  });
  return peopleByLocation;
};

const parseHydPeopleRecords = (people) => {
  const peopleByLocation = {};
  (people || []).forEach(person => {
    const workspaceRaw = person.wsNo;
    const name = person.name;
    const email = person.email;

    if (!workspaceRaw || !workspaceRaw.trim() || !name || !name.trim()) return;

    const workspace = normalizeHydLocation(workspaceRaw);
    if (!peopleByLocation[workspace]) {
      peopleByLocation[workspace] = [];
    }
    peopleByLocation[workspace].push({
      name: name.trim(),
      email: email ? email.trim() : ''
    });
  });
  return peopleByLocation;
};

// Pasadena CSV: building, location, name, email, ...
const parsePasadenaPeopleCsv = (lines, buildingName) => {
  const peopleByLocation = {};
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',');
    const building = values[0];
    const location = values[1];
    const name = values[2];
    const email = values[3];

    if (building === buildingName && name && name.trim()) {
      if (!peopleByLocation[location]) {
        peopleByLocation[location] = [];
      }
      peopleByLocation[location].push({
        name: name.trim(),
        email: email ? email.trim() : ''
      });
    }
  }
  return peopleByLocation;
};

// HYD CSV: Work_Space_No, Name, email — no building column. Normalizes the
// workspace number ("WS 015" → "WS015") so it lines up with the drawio ids.
// Non-workstation IDs (CABIN-1, HR ROOM, etc.) are kept as-is so they match
// the drawio element labels after HTML stripping.
const parseHydPeopleCsv = (lines) => {
  const peopleByLocation = {};
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',');
    const workspaceRaw = values[0];
    const name = values[1];
    const email = values[2];

    if (!workspaceRaw || !workspaceRaw.trim() || !name || !name.trim()) continue;

    const workspace = normalizeHydLocation(workspaceRaw);
    if (!peopleByLocation[workspace]) {
      peopleByLocation[workspace] = [];
    }
    peopleByLocation[workspace].push({
      name: name.trim(),
      email: email ? email.trim() : ''
    });
  }
  return peopleByLocation;
};
